import { useEffect } from "react";

import { isEditing } from "@builder.io/sdk-react";

/**
 * Builder visual-editor link-navigation guard (SFCC-11671).
 *
 * Inside the Builder visual editor the storefront runs in an iframe and
 * navigates via real anchors / React Router navigation, so clicking a
 * link-wrapped element (a category tile, a CTA, an "open in new tab" link)
 * makes the editor iframe follow the link / open a new tab instead of selecting
 * the element for editing. The old PWA Kit `@builder.io/react` integration
 * suppressed this; the Workers app (`@builder.io/sdk-react`) never ported an
 * equivalent guard.
 *
 * One delegated `click` listener on `document` (capture phase) cancels link
 * navigation while `isEditing()` is true:
 * - Capture runs before the browser reads the href, so it also covers
 *   `target="_blank"` (new-tab) links.
 * - Calling `preventDefault()` makes the SPA-delegation handler in
 *   `PrefetchBuilderAnchors` bail (it early-returns on `event.defaultPrevented`),
 *   so plain Builder `<a href>` blobs are covered without it re-navigating.
 * - It only acts when the click lands inside an anchor, so non-navigation
 *   clicks (selecting an element, dismissing a modal, switching tabbed
 *   sections) are left untouched, matching the ticket's requested behavior.
 *
 * Strictly gated on `isEditing()` (checked per-click), so the live preview and
 * the real storefront are completely unaffected — the listener is a no-op
 * outside the editor.
 *
 * Renders nothing.
 */
export function BuilderEditorLinkGuard() {
  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      // Only intervene inside the Builder visual editor.
      if (!isEditing()) return;
      const target = event.target as Element | null;
      const anchor = target?.closest<HTMLAnchorElement>("a[href]");
      if (!anchor) return;
      // A link click in the editor should select the element, not navigate.
      event.preventDefault();
    };
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);
  return null;
}
